import React, { useState, useEffect, useMemo, useContext, useCallback, FC } from 'react';
import axios from "axios";
import { Typography, ListBase, useSnackbar, ThemeProvider, Paper, DataTable, Breadcrumb, BreadcrumbItem, Box, ListSearchChangeProps, OnSelectAllChangeEvent } from '@pixel/core';
import Tooltip from '@pixel/core/Tooltip';
import Link from '@pixel/core/Link';
import CircularProgress from '@pixel/core/CircularProgress';
import { createInternalTheme } from "@pixel/internal/styles";
import { LayoutRoot, LayoutRootSection } from '@pixel/internal';
import TableChartIcon from '@pixel/assets/icons/TableChart';
import { useMultipleLocalizedText } from '../../../../contexts/Localization/localizationHooks';
import { 
    AssociationType,
    IBreadcrumbHierarchyObject, 
    IDepartmentsData, 
    IDepartmentsGetParms, 
    IDepartmentsResultData, 
    IDepartmentsSearchGetParms,
    IDimensionsProps,
    ISelectedDimensionsData,
    IStoredMappingData,
 } from '../../OdsTypes';
import OdsContext from '../../OdsContext';
import { getFullySelectedKeys, getLocalStorageDimensionData, getPartiallySelectedDimensionIds, getSelectedSubDimensionsCount, isAvailableInBreadcrumb, mergePreselectedAndActiveDimensions, setLocalStorageDimensionData, transformDepartmentsResult, updateBreadcrumb, getPartiallySelectedModifiedIds, updateDimensionLocalMapList, updateLocalSelectionState, getDynamicLengthForDimensions, handleSelectAllDimensions } from '../../OdsTransform';
import { SelectChangeEvent } from '@mui/material';
import { AddDimensionModalBreadcrumb } from '../modal/AddDimensionModalBreadcrumb';

const Departments: FC<IDimensionsProps> = ({
    accessGroupData,
    selectedDepartmentForAPIPayload,
    setSelectedDepartmentForAPIPayload,
    setCurrentDimensionSelectionList,
}) => {

    const OrgElementsContext = useContext(OdsContext);
    const {
        ODS_SELECTED,
        ODS_TAB_DEPARTMENTS,
        ODS_SUB_DEPARTMENTS,
        ODS_DESCRIPTION,
        ODS_STATUS,
        ODS_SEARCH_PLACEHOLDER,
        ODS_SEARCH_BUSINESS_UNITS,
        ODS_ROWS_PER_PAGE_LABEL,
        I18N_NO_RESULTS_FOUND,
        ODS_PARENT_DEPARTMENT,
    } = useMultipleLocalizedText();
    const theme = createInternalTheme({ darkMode: false, theme: {} });

    /*** styling */
    const jvDepartmentTitle = {
        textOverflow: 'ellipsis',
        overflow: 'hidden',
        wordBreak: 'break-all',
        whiteSpace: 'nowrap',
        display: 'inline-block',
    }

    const overlayStyling = {
        display: 'flex',
        justifyContent: 'center',
        height: '100%',
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        position: 'fixed',
        top: '0',
        left: '0',
        width: '100%',
        alignItems: 'center',
        zIndex: '1',
    }
    /*** End styling */

    const DEFAULT_STATUS = 'ACTIVE';
    const ODSPageSizeSessionStorage = () => JSON.parse(sessionStorage.getItem("ODS_PageSize")) ? JSON.parse(sessionStorage.getItem("ODS_PageSize")) : OrgElementsContext.defaultPageSize;
    const snackbar = useSnackbar();
    const partiallySelectedDepartmentIds = getPartiallySelectedDimensionIds('AG_department_list');

    /** memoized */
    const departmentsFetchParams: IDepartmentsGetParms = useMemo(() => ({
        pageSize: ODSPageSizeSessionStorage(),
        pageNumber: 0,
        status: DEFAULT_STATUS,
    }), []);
    const departmentsSearchParams: IDepartmentsGetParms = useMemo(() => ({
        pageSize: ODSPageSizeSessionStorage(),
        pageNumber: 0,
        keywords: '',
    }), []);
    /** end memoized */

    /*** state variables  */
    const [depFetchPayload, setDepFetchPayload] = useState<IDepartmentsGetParms>(departmentsFetchParams);
    const [depSearchPayload, setDepSearchPayload] = useState<IDepartmentsSearchGetParms>(departmentsSearchParams);
    const [results, setResults] = useState<IDepartmentsResultData>({ nodes: [], totalCount: 0, pageNumber: 0, pageSize: ODSPageSizeSessionStorage() });
    const [DepHierarchyTree, setDepHierarchyTree] = useState([{ name: 'All', eid: '' }]);
    const [perPageSize, setPerPageSize] = useState(ODSPageSizeSessionStorage());
    const [listLoading, setListLoading] = useState(false);
    const [searchText, setSearchText] = useState('');
    const [noSearchResult, setNoSearchResult] = useState(false);
    const [showInProgress, setShowInProgress] = useState(false);
    const [pagingContext, setPagingContext] = useState({
        hideFirstLastActions: true,
        perPage: perPageSize,
        page: 1,
        first: 1,
        last: perPageSize,
    });
    const [selectedDepartments, setSelectedDepartments] = useState<Array<ISelectedDimensionsData['eid']>>([])
    /*** end state variables */

    /*** use effects */
    useEffect(() => {
        fetchDepartmentList(depFetchPayload, accessGroupData ? accessGroupData.eid : null);
        setSearchText('');
    }, [depFetchPayload]);

    useEffect(() => {
        if (searchText && searchText.length > 1) {
            const accessGroupEid = accessGroupData ? accessGroupData.eid : null;
            const modifiedPartiallySelectedIds : Array<string> = getPartiallySelectedModifiedIds(partiallySelectedDepartmentIds, 'AG_department_list');
            const bodyPayload = {
                modifiedPartiallySelectedEIds: modifiedPartiallySelectedIds
            }
            axios.post(window.jvUrls.admin + '/action/accessGroups/departments/search/' + accessGroupEid, bodyPayload, {
                params: depSearchPayload
            })
                .then(function (response) {
                    const activeDepartments = response?.data?.activeDepartments;
                    setResults(transformDepartmentsResult(activeDepartments));
                    setSelectedDepartments(updateDimensionLocalMapList(response?.data?.associatedDepartmentsArray, 'AG_department_list', response.data.childrenForModifiedPartiallySelectedRequestedIds));
                    mergePreselectedAndActiveDimensions(activeDepartments?.departments ? activeDepartments?.departments : activeDepartments?.activeDepartments, 'namedDepartmentsData');
                    setCurrentDimensionSelectionList(getLocalStorageDimensionData('AG_department_list'));
                    setPagingContext({
                        hideFirstLastActions: true,
                        perPage: activeDepartments?.pageSize,
                        page: activeDepartments?.pageNumber + 1,
                        first: activeDepartments?.activeDepartments?.length > 0 ? activeDepartments?.pageNumber * activeDepartments?.pageSize + 1 : 0,
                        last: activeDepartments?.pageSize * activeDepartments?.pageNumber + activeDepartments?.activeDepartments?.length,
                    });
                    setListLoading(false);
                    activeDepartments?.totalCount === 0 ? setNoSearchResult(true) : setNoSearchResult(false);
                })
                .catch(function (error) {
                    console.log(error);
                    setListLoading(false);
                });
        }
    }, [searchText, depSearchPayload]);
    /*** End use effects */

    /*** xhr side effects */
    const fetchDepartmentList = async (depFetchPayload: IDepartmentsGetParms, accessGroupEid: string) => {
        try {
            setListLoading(true);
            const modifiedPartiallySelectedIds : Array<string> = getPartiallySelectedModifiedIds(partiallySelectedDepartmentIds, 'AG_department_list');
            const bodyPayload = {
                modifiedPartiallySelectedEIds: modifiedPartiallySelectedIds
            }
            await axios.post(window.jvUrls.admin + '/action/accessGroups/departments/' + accessGroupEid, bodyPayload, {
                params: depFetchPayload
            })
                .then((response) => {
                    const activeDepartments = response?.data?.activeDepartments;
                    const {activeDepartments: departments, pageSize, pageNumber} = activeDepartments;

                    setResults(transformDepartmentsResult(activeDepartments));
                    setSelectedDepartments(updateDimensionLocalMapList(response.data.associatedDepartmentsArray, 'AG_department_list', response.data.childrenForModifiedPartiallySelectedRequestedIds));
                    mergePreselectedAndActiveDimensions(activeDepartments?.departments ? activeDepartments?.departments : activeDepartments?.activeDepartments, 'namedDepartmentsData');
                    setCurrentDimensionSelectionList(getLocalStorageDimensionData('AG_department_list'));

                    // if (DepHierarchyTree.length === 1) {
                    //     sessionStorage.setItem("BU_company", JSON.stringify(departments[0]));
                    // }
                    departments?.length > 0 && setNoSearchResult(false);
                    setPagingContext({
                        hideFirstLastActions: true,
                        perPage: pageSize,
                        page: pageNumber + 1,
                        first: departments?.length > 0 ? pageNumber * pageSize + 1 : 0,
                        last: pageSize * pageNumber + departments?.length,
                    });
                    setListLoading(false);
                })
                .catch((error) => {
                    // show snackbar here
                    setListLoading(false);
                });
        } catch (error) {
            // show snackbar here
            console.log(error);
        }
    }

    const updatePayload = (department: IBreadcrumbHierarchyObject) => {
        if (department.eid === '') {
            setDepFetchPayload({ pageSize: ODSPageSizeSessionStorage(), pageNumber: 0, status: DEFAULT_STATUS });
        } else {
            setDepFetchPayload({ ...depFetchPayload, parentEId: department.eid, pageSize: ODSPageSizeSessionStorage(), pageNumber: 0, status: DEFAULT_STATUS });
        }
    }
    /*** end xhr side effects */

    /*** event handlers */
    const onBreadcrumChange = (department: IBreadcrumbHierarchyObject) => {
        setDepHierarchyTree(updateBreadcrumb(DepHierarchyTree, department));
        updatePayload(department);
    }

    const resetSearch = () => {
        if (searchText !== '') {
            setDepHierarchyTree([{ name: 'All', eid: '' }]);
            setDepFetchPayload({ pageSize: ODSPageSizeSessionStorage(), pageNumber: 0, status: DEFAULT_STATUS });
        }
    }

    const handleSearch = (props: ListSearchChangeProps) => {
        setSearchText(props.q);
        if (props.q.length > 1) {
            setDepSearchPayload({ ...depSearchPayload, pageSize: ODSPageSizeSessionStorage(), pageNumber: 0, keywords: encodeURIComponent(props.q) })
            setListLoading(true);
        } else {
            setNoSearchResult(false);
            resetSearch();
        }
    };

    const onHandleDepClick = (departments: IBreadcrumbHierarchyObject) => {
        const newEntry = { name: departments.name, eid: departments.eid };
        setSearchText('');
        !isAvailableInBreadcrumb(DepHierarchyTree, departments) && setDepHierarchyTree((DepHierarchyTree) => [...DepHierarchyTree, newEntry]);
        setDepFetchPayload({ ...depFetchPayload, parentEId: departments.eid, pageNumber: 0, pageSize: ODSPageSizeSessionStorage() });
    }

    const onPageChanged = (event: any, currPage: number) => {
        if (searchText.length > 1) {
            setDepSearchPayload({ ...depSearchPayload, pageSize: ODSPageSizeSessionStorage(), pageNumber: currPage - 1 });
        } else {
            if (DepHierarchyTree[DepHierarchyTree.length - 1].eid === '') {
                setDepFetchPayload({ pageSize: ODSPageSizeSessionStorage(), pageNumber: currPage - 1, status: DEFAULT_STATUS });
            } else {
                setDepFetchPayload({ ...depFetchPayload, pageSize: ODSPageSizeSessionStorage(), parentEId: DepHierarchyTree[DepHierarchyTree.length - 1].eid, pageNumber: currPage - 1, status: DEFAULT_STATUS });
            }
        }
    }

    const onPaginationChange = (event: SelectChangeEvent) => {
        sessionStorage.setItem("ODS_PageSize", JSON.stringify(Number(event.target.value)));
        setPerPageSize(Number(event.target.value));
        if (searchText !== '') {
            setDepSearchPayload({ ...depSearchPayload, pageSize: ODSPageSizeSessionStorage(), pageNumber: 0, keywords: encodeURIComponent(searchText) })
            setListLoading(true);
        } else {
            updatePayload(DepHierarchyTree[DepHierarchyTree.length - 1]);
        }
    }

    const onHandleSearchedDepClick = (department: IBreadcrumbHierarchyObject, DepTree: Array<IBreadcrumbHierarchyObject>) => {
        let newEntry;
        if (DepTree) {
            let index = DepTree.findIndex(dep => dep.eid === department.eid);
            let DepBreadcrumb = DepTree.slice(0, index + 1);
            newEntry = [{ name: 'All', eid: '' }, ...DepBreadcrumb];
        } else {
            newEntry = [{ name: 'All', eid: '' }, { name: department.name, eid: department.eid }];
        }
        setSearchText('');
        setDepHierarchyTree(newEntry);
        setDepFetchPayload({ ...depFetchPayload, parentEId: department.eid, pageNumber: 0, pageSize: ODSPageSizeSessionStorage() });
    }

    useEffect(() => {
        const modifiedSelectedDepartments: Array<ISelectedDimensionsData> = [];
        selectedDepartments?.forEach((value: string) => {
            modifiedSelectedDepartments.push({eid: value, associationType: AssociationType.FULLY_ASSOCIATED});
        });
        setSelectedDepartmentForAPIPayload(modifiedSelectedDepartments);
        selectedDepartments?.length === 0 && setSelectedDepartmentForAPIPayload([]);
    }, [selectedDepartments]);

    const handleSelection = useCallback((node: ISelectedDimensionsData, nextSelectedState: boolean) => {
        const prevAssociatedData: IStoredMappingData = getLocalStorageDimensionData('AG_department_list');
        
        if (nextSelectedState) {
            setSelectedDepartments([...selectedDepartments, node.eid])
        } else {
            setSelectedDepartments(selectedDepartments.filter(item => item !== node.eid))
        }
        setSelectedDepartments(updateLocalSelectionState(node.eid, nextSelectedState, 'AG_department_list', prevAssociatedData));
    }, [selectedDepartments])

    /**
    * handleDepartmentSelectAll Manipulates the click event to selectAll list items
    *
    * @param {any} event - event object
    * @param {any} eventType - eventType - selectAll, unselectAll, etc
    * @param {any} context - context of the event
    */
    const handleDepartmentSelectAll: OnSelectAllChangeEvent = (event: any, eventType: any, context: any) => {
        const prevLocalList: IStoredMappingData = getLocalStorageDimensionData('AG_department_list');
        const localList: IStoredMappingData = handleSelectAllDimensions(eventType, 'AG_department_list', prevLocalList);
        
        setLocalStorageDimensionData('AG_department_list', localList);
        setSelectedDepartments(getFullySelectedKeys([localList?.dimensionIdToDimensionList]));
    }

    /*** end event handlers */

    /*** render helpers */
    const populateTitle = (node: IDepartmentsData) => {
        if (node?.subChildrenCount > 0) {
            return <Tooltip title={node.name} arrow placement="bottom"><Link underline="hover" sx={{ ...jvDepartmentTitle, maxWidth: '100%', fontWeight: '600' }} onClick={() => (node.ancestors && searchText.length > 1) ? onHandleSearchedDepClick({ name: node.name, eid: node.eid }, [...node.ancestors, { name: node.name, eid: node.eid }]) : onHandleDepClick(node)} variant="body2">{node.name}</Link></Tooltip>;
        } else {
            return <Tooltip title={node.name} arrow placement="bottom"><span><Typography variant="body2" sx={{ ...jvDepartmentTitle, maxWidth: '100%' }} disabled={!node.active}>{node.name}</Typography></span></Tooltip>;
        }
    }

	const renderParentDep = (node: IDepartmentsData) => {
		const list: any = [];
		if (node.ancestors && node.ancestors.length > 0) {
			node.ancestors.map((parent, index) => {
				list.push(
					<span style={{ display: 'inline-flex' }}>
						<Tooltip key={parent.eid} title={parent.name} arrow placement="bottom"><Link underline="hover" sx={{ ...jvDepartmentTitle, maxWidth: '160px', fontWeight: '600' }} onClick={() => onHandleSearchedDepClick({ name: parent.name, eid: parent.eid }, node.ancestors)} variant="body2">{parent.name}</Link></Tooltip>
						<span style={{ padding: '0 6px' }}>{index < node.ancestors.length - 1 && ' /   '}</span>
					</span>
				);
			})
		}
		return list;
	}

    /*** end render helpers */

    const columns = useMemo(() => [
        { id: 'departments', columnName: ODS_TAB_DEPARTMENTS, maxWidth: 250, renderCell: (node: IDepartmentsData) => populateTitle(node) },
        { id: 'SubDepartmentsSelected', columnName: `${ODS_SUB_DEPARTMENTS || ''} ${ODS_SELECTED || ''}`, renderCell: (node: ISelectedDimensionsData) => getSelectedSubDimensionsCount(node?.eid, 'AG_department_list'), minWidth: 80 },
        { id: 'subdepartments', columnName: ODS_SUB_DEPARTMENTS || '', renderCell: (node: IDepartmentsData) => node.subChildrenCount, minWidth: 80 },
    ], []);

    return (
        <React.StrictMode>
            <ThemeProvider theme={theme}>
                <LayoutRoot>
                    <LayoutRootSection top bottom transparent className='jv-tab-panel-wrapper'>
                        <AddDimensionModalBreadcrumb
                            DimensionHierarchyTree={DepHierarchyTree}
                            onBreadcrumChange={onBreadcrumChange}
                            searchText={searchText}
                        />
                        {(showInProgress && <Box sx={overlayStyling}><CircularProgress size={60} /></Box>)}
                        <Box className='content-wrapper'>
                            <ListBase
                                className
                                showSearch
                                labelSearch={ODS_SEARCH_PLACEHOLDER}
                                labelSearchAria={ODS_SEARCH_BUSINESS_UNITS}
                                // @ts-ignore
                                results={results} // has the pager content
                                selectable
                                selectedIds={selectedDepartments}
                                partiallySelectedIds={partiallySelectedDepartmentIds}
                                onSelection={handleSelection}
                                onSelectAll={DepHierarchyTree?.length === 1 && handleDepartmentSelectAll}
                                allSelected={getDynamicLengthForDimensions(selectedDepartments, results?.nodes) === results?.nodes?.length}
                                onSearch={handleSearch}
                                appliedSearchQuery={searchText}
                                pagingContext={pagingContext}
                                onPageChanged={onPageChanged}
                                onPaginationChange={(event: SelectChangeEvent) => onPaginationChange(event)}
                                rowsPerPageLabel={ODS_ROWS_PER_PAGE_LABEL}
                                resultsPerPage={perPageSize}
                                perPageOptions={OrgElementsContext.perPageSelectOptions}
                                viewContexts={[
                                    {
                                        ariaLabel: 'Datatable View',
                                        icon: <TableChartIcon />,
                                        renderResults: (viewContextProps) => {
                                            if (listLoading) {
                                                return (<div className={'bu-list-loader'} style={{ display: 'flex', justifyContent: 'center' }}>
                                                    <CircularProgress size={34} />
                                                </div>)
                                            } else {
                                                if (!noSearchResult) {
                                                    return (
                                                        <DataTable
                                                            {...viewContextProps}
                                                            getRowID={(node: IDepartmentsData) => node?.eid}
                                                            columns={columns}
                                                            labelCheckboxAriaLabel={ODS_SELECTED || ''}
                                                            labelCheckboxKey='eid'
                                                        />
                                                    )
                                                } else {
                                                    return (<div className={'bu-list-no-results'}><Paper sx={{ display: 'flex', justifyContent: 'center', padding: '16px' }}> {I18N_NO_RESULTS_FOUND}</Paper></div>)
                                                }
                                            }
                                        }
                                    }
                                ]}
                            />
                        </Box>
                    </LayoutRootSection>
                </LayoutRoot>
            </ThemeProvider>
        </React.StrictMode>
    )
}

export default Departments;
